import { ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { CryptoCurrencyId, CryptoCurrencyMeta } from 'components/features/rewards/rewardsPane/types/cryptoCurrency'
import { formatPercent } from 'helpers/formatHelpers'
import { CryptoRewardsCurrencyPickerPreset } from 'components/features/rewards/rewardsPane/components/CryptoRewardsCurrencyPickerPreset/CryptoRewardsCurrencyPickerPreset'
import Button from 'components/features/pfm/Button/Button'
import cn from 'classnames'
import styles from 'components/features/rewards/rewardsPane/components/CryptoRewardsCurrencyPickerWithMarketDetails/CryptoRewardsCurrencyPickerWithMarketDetails.styl'
import Touchable from 'components/utils/Touchable'

export type CryptoRewardsCurrencyPickerWithMarketDetailsProps = {
  isLoading: boolean
  children: CryptoCurrencyMeta[]
  getIconByCryptoId (id: CryptoCurrencyId): ReactNode
  onChoose (id: CryptoCurrencyId): void
}

export function CryptoRewardsCurrencyPickerWithMarketDetails ({
  isLoading,
  children,
  getIconByCryptoId,
  onChoose,
}: CryptoRewardsCurrencyPickerWithMarketDetailsProps): ReactElement {
  const getRowKey = useCallback(({ id }: CryptoCurrencyMeta): string => id, [])
  const getCurrencyIconByRowData = useCallback(({ id }: CryptoCurrencyMeta): ReactNode => getIconByCryptoId(id), [getIconByCryptoId])

  const intl = useMemo(() => Intl.NumberFormat('en', { notation: 'compact' }), [])

  const getCurrencyDescriptionByRowData = useCallback(({ id, name, marketCapUSD, changePercent }: CryptoCurrencyMeta): ReactNode => {
    const displayPercent = formatPercent(changePercent / 100, 'one')
    const isDisplayZero = displayPercent === '0%'
    return (
      <Touchable onClick={ () => onChoose(id) }>
        <div className={ styles.currencyDescriptionWrapper }>
          <div className={ styles.currencyDescriptionFirstRow }>
            <h5 className={ styles.currencyName }>{ name }</h5>
            <p className={ styles.currencyMarketCap }>${ intl.format(marketCapUSD) }</p>
          </div>
          <div className={ styles.currencyDescriptionSecondRow }>
            <span>

              <small
                className={ cn(styles.currencyChangePercent, {
                  [styles.fall]: !isDisplayZero && changePercent < 0,
                  [styles.rise]: !isDisplayZero && changePercent > 0,
                }) }
              >
                { !isDisplayZero && changePercent > 0 && '+' }
                { displayPercent }
              </small>
              <small>
                last 30 days
              </small>
            </span>
            <small>
              market cap
            </small>
          </div>
        </div>
      </Touchable>
    )
  }, [intl, onChoose])

  const getSelectButtonByRowData = useCallback(({ id }: CryptoCurrencyMeta): ReactNode => {
    const handleChoose = () => onChoose(id)
    return <Button size='mini' className={ styles.button } onClick={ handleChoose }>Select</Button>
  }, [onChoose])

  return (
    <CryptoRewardsCurrencyPickerPreset<CryptoCurrencyMeta>
      isLoading={ isLoading }
      className={ styles.tableContainer }
      getRowKey={ getRowKey }
      getCurrencyIconByRowData={ getCurrencyIconByRowData }
      getCurrencyDescriptionByRowData={ getCurrencyDescriptionByRowData }
      getSelectButtonByRowData={ getSelectButtonByRowData }
    >
      { children }
    </CryptoRewardsCurrencyPickerPreset>
  )
}
